import { useState } from 'react';
import Icon from './Icon';
import DateField from './DateField';
import CoverPicker, { dayFromMs, endOfDayMs } from './CoverPicker';
import { useApp } from '../store';
import { displayName, initials } from '../people';
import { humanDay } from '../when';

/**
 * ── WHEN I'M AWAY — a person nominates who signs for them — sign-offs R1 (0130) ─
 *
 * WHY. A person asked to sign work off goes on leave, and the work waits on a
 * signature nobody can give. Here they nominate a teammate to sign on their behalf
 * while they are away. The nominee signs as themselves and every signature is
 * stamped "on behalf of" — the on-behalf engine already carries this (0122), so
 * the trail is unchanged; this screen only records who covers whom.
 *
 * COVER IS ALL BOARDS. It is not tied to one board — it applies to the person's
 * pending and future sign-offs across every board they are on. So the copy names
 * the workspace, not a board.
 *
 * THREE STATES. No cover yet (the empty state, offering to choose somebody); a
 * live cover (the nominee, an optional end date, and an on/off); and a paused
 * cover — the row is kept when it is turned off, so the person's last nominee is
 * remembered and one tap turns it back on. Turning it off stops new signatures at
 * once; anything already signed stays on the record.
 */

export default function WhenAway({ workspaceName }: { workspaceName: string }) {
  const user = useApp((s) => s.user);
  const profiles = useApp((s) => s.profiles);
  const access = useApp((s) => s.access);
  const covers = useApp((s) => s.signoffCover);
  const setCover = useApp((s) => s.setCover);
  const clearCover = useApp((s) => s.clearCover);

  const me = user?.email?.toLowerCase() ?? '';
  const mine = covers.find((c) => c.delegator === me);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [picking, setPicking] = useState(false);

  /* Named where it can be, so a glance answers "across what". */
  const where = workspaceName ? `across ${workspaceName}` : 'across your boards';
  const nomineeName = mine ? displayName(mine.delegate, profiles) : '';
  const nomineePosition = mine ? profiles[mine.delegate]?.title?.trim() ?? '' : '';

  const nominate = async (delegate: string) => {
    setBusy(true);
    setProblem('');
    const msg = await setCover({ delegate, until: mine?.until ?? null });
    setBusy(false);
    setPicking(false);
    if (msg) setProblem(msg);
  };
  const changeUntil = async (iso: string) => {
    if (!mine) return;
    setBusy(true);
    setProblem('');
    const msg = await setCover({ delegate: mine.delegate, until: endOfDayMs(iso) });
    setBusy(false);
    if (msg) setProblem(msg);
  };
  const toggle = async () => {
    if (!mine) return;
    setBusy(true);
    setProblem('');
    const msg = mine.active
      ? await clearCover()
      : await setCover({ delegate: mine.delegate, until: mine.until ?? null, active: true });
    setBusy(false);
    if (msg) setProblem(msg);
  };

  return (
    <>
      <h2>When I&rsquo;m away</h2>
      <p className="hint cover-intro">
        Nominate a teammate to sign your sign-offs while you&rsquo;re away. They sign as themselves
        and every signature is stamped as made on your behalf, so the trail always shows who
        actually signed. Cover applies {where}.
      </p>

      {problem && (
        <p className="cover-problem" role="alert">
          {problem}
        </p>
      )}

      {picking ? (
        <CoverPicker
          me={me}
          emails={access.emails}
          profiles={profiles}
          busy={busy}
          onPick={nominate}
          onCancel={() => setPicking(false)}
        />
      ) : !mine ? (
        /* NO COVER YET — the empty state. */
        <div className="cover-empty">
          <span className="cover-empty-ic" aria-hidden="true">
            <Icon name="userPlus" size={22} />
          </span>
          <p className="cover-empty-h">No cover set — you answer your own sign-offs</p>
          <p className="cover-empty-sub">
            Set this before you go on leave and any pending or new sign-off can be signed for you
            until you&rsquo;re back.
          </p>
          <button className="btn btn-primary" onClick={() => setPicking(true)}>
            Choose who covers for me
          </button>
        </div>
      ) : (
        /* A COVER IS SET — live, or paused (kept so the nominee is remembered). */
        <div className={`cover-card ${mine.active ? 'is-on' : 'is-off'}`}>
          <div className="cover-card-head">
            <span className="cover-av" aria-hidden="true">
              {initials(mine.delegate, profiles)}
            </span>
            <div className="cover-who">
              <div className="cover-who-name">{nomineeName}</div>
              <div className="cover-who-sub">
                {nomineePosition ? `${nomineePosition} · ` : ''}covers you {where}
              </div>
            </div>
            <button className="cover-change" onClick={() => setPicking(true)}>
              Change
            </button>
          </div>

          <div className="cover-grid">
            <div className="cover-field">
              <label className="cover-lbl" htmlFor="cover-until-anchor">
                Who signs for me <span className="req">*</span>
              </label>
              <span className="sr-signer">
                <span className="sr-signer-av" aria-hidden="true">
                  {initials(mine.delegate, profiles)}
                </span>
                <span className="sr-signer-name">{nomineeName}</span>
                {nomineePosition && <span className="sr-signer-sub">{nomineePosition}</span>}
                {/* The chip's × TURNS COVER OFF (keeping the remembered nominee) —
                    the quick "stop" the toggle also does. It is shown only while
                    cover is on, so it is never a no-op; swapping the nominee is the
                    "Change" link above, so the two actions never overlap. */}
                {mine.active && (
                  <button
                    className="sr-signer-x"
                    aria-label="Turn cover off"
                    disabled={busy}
                    onClick={() => void toggle()}
                  >
                    <Icon name="close" size={13} />
                  </button>
                )}
              </span>
            </div>

            <div className="cover-field">
              <span className="cover-lbl" id="cover-until-anchor">
                Until
              </span>
              <DateField
                ariaLabel="Cover ends on"
                variant="form"
                value={dayFromMs(mine.until)}
                placeholder="No end date"
                min={dayFromMs(Date.now())}
                onChange={(iso) => void changeUntil(iso)}
              />
            </div>

            <div className="cover-field cover-field-toggle">
              <span className="cover-lbl">Cover</span>
              <div className="cover-switch-row">
                <button
                  className={`switch ${mine.active ? 'on' : ''}`}
                  role="switch"
                  aria-checked={mine.active}
                  aria-label="Cover on"
                  disabled={busy}
                  onClick={() => void toggle()}
                >
                  <span className="switch-knob" />
                </button>
                <span className="cover-switch-state">{mine.active ? 'On' : 'Off'}</span>
              </div>
            </div>
          </div>

          {mine.active ? (
            <div className="cover-banner cover-banner-on">
              <span className="cover-dot" aria-hidden="true" />
              <span>
                On now — {nomineeName} can sign all your pending and future sign-offs {where}
                {mine.until ? ` until ${humanDay(dayFromMs(mine.until))}` : ''}.
              </span>
            </div>
          ) : (
            <div className="cover-banner cover-banner-off">
              <span>
                Off — you answer your own sign-offs. Turn it on to let {nomineeName} sign for you
                again.
              </span>
            </div>
          )}
        </div>
      )}

      {mine && !picking && (
        <p className="hint cover-foot">
          Leave the end date blank to keep cover on until you turn it off. Turning it off stops new
          signatures at once; anything {nomineeName} has already signed stays on the record, stamped
          as done on your behalf.
        </p>
      )}
    </>
  );
}
